import type { InputHTMLAttributes } from 'react';

/**
 * One form field (docs/design-system.md, "Field"): a real `<label>`, the control, then at most one
 * line under it. An error replaces the hint, never joins it. The error is `role="alert"` so a
 * screen reader announces it when it appears, and `aria-describedby` ties it to the control so it
 * is read again on focus.
 */
export function Field({
  id,
  label,
  hint,
  error,
  ...input
}: {
  id: string;
  label: string;
  hint?: string;
  error?: string | null;
} & Omit<InputHTMLAttributes<HTMLInputElement>, 'id'>): JSX.Element {
  const noteId = `${id}-note`;
  return (
    <div className="field">
      <label htmlFor={id}>{label}</label>
      <input
        {...input}
        id={id}
        aria-invalid={error ? true : undefined}
        aria-describedby={error || hint ? noteId : undefined}
      />
      {error ? (
        <p id={noteId} className="field-error" role="alert">
          {error}
        </p>
      ) : (
        hint && (
          <p id={noteId} className="field-hint">
            {hint}
          </p>
        )
      )}
    </div>
  );
}
